import { apiRequest } from './http';
import { inventoryApi } from './inventory';
import { journalsApi } from './journals';
import type {
  AccountLedger,
  AccountSummary,
  BankAccount,
  CashBox,
  CashLimitAlert,
  CashTransaction,
  CustomerCreditNote,
  CustomerInvoice,
  CustomerReceipt,
  Customer,
  DashboardSnapshot,
  DataEnvelope,
  DepositBatch,
  DunningActivity,
  ExchangeRateSummary,
  ReceivableWriteOff,
  InventoryWorkspace,
  Journal,
  JournalPage,
  JournalQuery,
  PayableAgingReport,
  PayableBalanceBreakdown,
  PaymentProposal,
  PaymentRun,
  PayablesWorkspace,
  Person,
  PersonLedger,
  ReceivableAgingReport,
  ReceivableOpenItem,
  ReceivablesWorkspace,
  ReconciliationDetail,
  StatementImport,
  StatementJournalProposal,
  StatementMappingProfile,
  Supplier,
  SupplierBill,
  SupplierPayment,
  TreasuryWorkspace,
  TrialBalance,
  WorkspaceBranch,
  WorkspaceCurrency,
  WorkspaceFiscalPeriod,
  WorkspaceScope,
} from './types';

async function fromApi<T>(operation: () => Promise<T>): Promise<DataEnvelope<T>> {
  return { data: await operation(), source: 'api' };
}


// Composite workspaces retain their envelope while every part is authoritative API data.
function combine<T>(data: T, parts: readonly DataEnvelope<unknown>[]): DataEnvelope<T> {
  void parts;
  return { data, source: 'api' };
}

/** A date-only value taken from the browser's own calendar, so a late-evening tenant east of UTC still gets today. */
function isoDate(value: Date): string {
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${value.getFullYear()}-${month}-${day}`;
}

/**
 * The payment proposal is drawn for a window the server requires: the date the run would settle on, and the
 * horizon of due dates to include. No currency filter is sent, so bills in every currency are proposed.
 */
function paymentProposalWindow(horizonDays = 7): string {
  const today = new Date();
  const dueThrough = new Date(today);
  dueThrough.setDate(dueThrough.getDate() + horizonDays);
  return `?paymentDate=${isoDate(today)}&dueThrough=${isoDate(dueThrough)}`;
}

function functionalCurrencyOf(currencies: readonly WorkspaceCurrency[]): string {
  return currencies.find((currency) => currency.isFunctional)?.code ?? 'IRR';
}

export const varzaApi = {
  dashboard(): Promise<DataEnvelope<DashboardSnapshot>> {
    return apiRequest<DashboardSnapshot>('/dashboard').then((data) => ({ data, source: 'api' as const }));
  },

  /** The journal register: one filtered, ordered, paged page of the tenant's entries. */
  journals(query: JournalQuery = {}): Promise<DataEnvelope<JournalPage>> {
    return fromApi(() => journalsApi.list(query));
  },

  journal(journalId: string): Promise<DataEnvelope<Journal>> {
    return fromApi(() => journalsApi.get(journalId));
  },

  /** One account's own general ledger page. */
  accountLedger(
    accountId: string,
    fromDate?: string,
    toDate?: string,
    includeDescendants = false,
  ): Promise<DataEnvelope<AccountLedger>> {
    return fromApi(() => journalsApi.accountLedger(accountId, fromDate, toDate, includeDescendants));
  },

  trialBalance(fromDate?: string, toDate?: string): Promise<DataEnvelope<TrialBalance>> {
    return fromApi(() => journalsApi.trialBalance(fromDate, toDate));
  },

  accounts(): Promise<DataEnvelope<readonly AccountSummary[]>> {
    return fromApi(() => apiRequest<readonly AccountSummary[]>('/accounts'));
  },

  persons(): Promise<DataEnvelope<readonly Person[]>> {
    return fromApi(() => apiRequest<readonly Person[]>('/persons'));
  },

  personLedger(personId: string, fromDate?: string, toDate?: string): Promise<DataEnvelope<PersonLedger>> {
    const params = new URLSearchParams();
    if (fromDate) params.set('fromDate', fromDate);
    if (toDate) params.set('toDate', toDate);
    const suffix = params.size ? `?${params.toString()}` : '';
    return fromApi(() => apiRequest<PersonLedger>(`/persons/${personId}/ledger${suffix}`));
  },

  exchangeRates(): Promise<DataEnvelope<readonly ExchangeRateSummary[]>> {
    return fromApi(() => apiRequest<readonly ExchangeRateSummary[]>('/exchange-rates'));
  },

  /**
   * The inventory workspace: master data, live balances, and the documents that move stock. Each part is
   * fetched from its own endpoint and every value remains authoritative server data.
   */
  async inventoryWorkspace(): Promise<DataEnvelope<InventoryWorkspace>> {
    const [
      products,
      warehouses,
      locations,
      lots,
      balances,
      movements,
      reservations,
      transfers,
      counts,
      valuation,
      serials,
      units,
      unitConversions,
      accountMappings,
      reconciliation,
    ] = await Promise.all([
      fromApi(() => inventoryApi.products()),
      fromApi(() => inventoryApi.warehouses()),
      fromApi(() => inventoryApi.locations()),
      fromApi(() => inventoryApi.lots()),
      fromApi(() => inventoryApi.balances()),
      fromApi(() => inventoryApi.movements({ limit: 50 })),
      fromApi(() => inventoryApi.reservations(true)),
      fromApi(() => inventoryApi.transfers()),
      fromApi(() => inventoryApi.counts()),
      fromApi(() => inventoryApi.valuation()),
      fromApi(() => inventoryApi.serials()),
      fromApi(() => inventoryApi.units()),
      fromApi(() => inventoryApi.unitConversions()),
      fromApi(() => inventoryApi.accountMappings()),
      fromApi(() => inventoryApi.reconciliation()),
    ]);

    return combine(
      {
        products: products.data,
        warehouses: warehouses.data,
        locations: locations.data,
        lots: lots.data,
        balances: balances.data,
        movements: movements.data,
        reservations: reservations.data,
        transfers: transfers.data,
        counts: counts.data,
        valuation: valuation.data,
        serials: serials.data,
        units: units.data,
        unitConversions: unitConversions.data,
        accountMappings: accountMappings.data,
        reconciliation: reconciliation.data,
      },
      [
        products,
        warehouses,
        locations,
        lots,
        balances,
        movements,
        reservations,
        transfers,
        counts,
        valuation,
        serials,
        units,
        unitConversions,
        accountMappings,
        reconciliation,
      ],
    );
  },

  async workspaceScope(): Promise<DataEnvelope<WorkspaceScope>> {
    // Branch is an analytic dimension the General Ledger has not built master data for yet, so no
    // deployment answers `/branches`. Asking anyway cost the scope its periods and currencies as
    // well, because all three arrive on one round trip and a single rejection loses the batch. The
    // dimension therefore reads as empty — which the branch control already renders as "all
    // branches" — until the ledger publishes the route, at which point it is fetched like the rest.
    const branches: readonly WorkspaceBranch[] = [];
    const [fiscalPeriods, currencies] = await Promise.all([
      fromApi(() => apiRequest<readonly WorkspaceFiscalPeriod[]>('/fiscal-periods')),
      fromApi(() => apiRequest<readonly WorkspaceCurrency[]>('/currencies')),
    ]);

    return {
      data: { branches, fiscalPeriods: fiscalPeriods.data, currencies: currencies.data },
      source: 'api',
    };
  },

  async receivablesWorkspace(): Promise<DataEnvelope<ReceivablesWorkspace>> {
    const [customers, invoices, openItems, receipts, creditNotes, aging, writeOffs, collections] = await Promise.all([
      fromApi(() => apiRequest<readonly Customer[]>('/customers')),
      fromApi(() => apiRequest<readonly CustomerInvoice[]>('/customer-invoices')),
      fromApi(() => apiRequest<readonly ReceivableOpenItem[]>('/receivables/open-items')),
      fromApi(() => apiRequest<readonly CustomerReceipt[]>('/customer-receipts')),
      fromApi(() => apiRequest<readonly CustomerCreditNote[]>('/customer-credit-notes')),
      fromApi(() => apiRequest<ReceivableAgingReport>('/receivables/aging')),
      fromApi(() => apiRequest<readonly ReceivableWriteOff[]>('/receivable-write-offs')),
      fromApi(() => apiRequest<readonly DunningActivity[]>('/receivable-collections')),
    ]);

    return combine(
      {
        customers: customers.data,
        invoices: invoices.data,
        openItems: openItems.data,
        receipts: receipts.data,
        creditNotes: creditNotes.data,
        aging: aging.data,
        writeOffs: writeOffs.data,
        collections: collections.data,
      },
      [customers, invoices, openItems, receipts, creditNotes, aging, writeOffs, collections],
    );
  },

  async payablesWorkspace(): Promise<DataEnvelope<PayablesWorkspace>> {
    const [suppliers, bills, payments, paymentRuns, aging, proposal, breakdown, currencies] = await Promise.all([
      fromApi(() => apiRequest<readonly Supplier[]>('/suppliers')),
      fromApi(() => apiRequest<readonly SupplierBill[]>('/supplier-bills')),
      fromApi(() => apiRequest<readonly SupplierPayment[]>('/supplier-payments')),
      fromApi(() => apiRequest<readonly PaymentRun[]>('/payment-runs')),
      fromApi(() => apiRequest<PayableAgingReport>('/payables/aging')),
      fromApi(() => apiRequest<PaymentProposal>(`/payables/payment-proposal${paymentProposalWindow()}`)),
      fromApi(() => apiRequest<PayableBalanceBreakdown>('/payables/balance-breakdown')),
      fromApi(() => apiRequest<readonly WorkspaceCurrency[]>('/currencies')),
    ]);

    return combine(
      {
        suppliers: suppliers.data,
        bills: bills.data,
        payments: payments.data,
        paymentRuns: paymentRuns.data,
        aging: aging.data,
        proposal: proposal.data,
        breakdown: breakdown.data,
        functionalCurrencyCode: functionalCurrencyOf(currencies.data),
      },
      [suppliers, bills, payments, paymentRuns, aging, proposal, breakdown, currencies],
    );
  },

  async treasuryWorkspace(): Promise<DataEnvelope<TreasuryWorkspace>> {
    const [bankAccounts, cashBoxes, cashTransactions, deposits, statementImports, reconciliations, mappingProfiles, journalProposals, cashLimitAlerts] = await Promise.all([
      fromApi(() => apiRequest<readonly BankAccount[]>('/bank-accounts')),
      fromApi(() => apiRequest<readonly CashBox[]>('/cash-boxes')),
      fromApi(() => apiRequest<readonly CashTransaction[]>('/cash-transactions')),
      fromApi(() => apiRequest<readonly DepositBatch[]>('/deposit-batches')),
      fromApi(() => apiRequest<readonly StatementImport[]>('/bank-statement-imports')),
      fromApi(() => apiRequest<readonly ReconciliationDetail[]>('/bank-reconciliations')),
      fromApi(() => apiRequest<readonly StatementMappingProfile[]>('/statement-mapping-profiles')),
      fromApi(() => apiRequest<readonly StatementJournalProposal[]>('/statement-journal-proposals')),
      fromApi(() => apiRequest<readonly CashLimitAlert[]>('/cash-boxes/limit-alerts')),
    ]);

    return combine(
      { bankAccounts: bankAccounts.data, cashBoxes: cashBoxes.data, cashTransactions: cashTransactions.data, deposits: deposits.data, statementImports: statementImports.data, reconciliations: reconciliations.data, mappingProfiles: mappingProfiles.data, journalProposals: journalProposals.data, cashLimitAlerts: cashLimitAlerts.data },
      [bankAccounts, cashBoxes, cashTransactions, deposits, statementImports, reconciliations, mappingProfiles, journalProposals, cashLimitAlerts],
    );
  },
};
